import type { StrategyItemResponseDTO, PortfolioItemResponseDTO } from '@/api-v2/web/discover';
import { isStrategyItemResponseDTO } from '@/api-v2/web/discover';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import { isPortfolioTreeSubportfolio, isPortfolioTreeStrategy } from '@/types/IPortfolioTree';

/**
 * Returns the symbols used for pinning items in the colorMap on Factsheets.
 *
 * For portfolios, we use the portfolio tree id (to maintain consistency with the rest of the platform).
 *
 * For strategies, we use the strategy code.
 */
export function useGetColorMapIds({
  benchmark,
}: {
  benchmark?: Ref<StrategyItemResponseDTO | PortfolioItemResponseDTO | null | undefined>;
} = {}): {
  /**
   * The symbol used for pinning the item under analysis in the colorMap:
   * - uses the portfolio tree id for subportfolios or portfolio tree items
   * - uses the strategy code for strategies
   *
   * Compared to the `indexUniqueIdentifier`, which:
   * - uses the portfolio slug (or reference code) for portfolio-like items
   * - uses the strategy code for strategies
   */
  itemUnderAnalysisColorMapId: Ref<string | null>;

  /**
   * The symbol used for pinning the benchmark in the colorMap on Factsheets:
   * - uses the portfolio tree id for portfolios
   * - uses the strategy code for strategies
   *
   * Compared to the `benchmarkCode` from `useBenchmark`, which:
   * - uses the portfolio slug for portfolios
   * - uses the strategy code for strategies
   */
  benchmarkColorMapId: Ref<string | null>;

  /**
   * Compound symbol formed by the item under analysis color map symbol and the benchmark color map symbol
   */
  activeReturnTrackColorMapId: Ref<string | null>;
} {
  const { itemUnderAnalysis } = useItemUnderAnalysisGlobalState();

  const itemUnderAnalysisColorMapId = computed((): string | null => {
    if (!itemUnderAnalysis.value) return null;

    if (isPortfolioTreeSubportfolio(itemUnderAnalysis.value) || isPortfolioTreeStrategy(itemUnderAnalysis.value)) {
      return itemUnderAnalysis.value.portfolioTreeId;
    }

    if (isStrategyFactsheetResponseDTO(itemUnderAnalysis.value)) {
      return itemUnderAnalysis.value.strategy.code;
    }

    return null;
  });

  const benchmarkColorMapId = computed((): string | null => {
    if (benchmark?.value == null) return null;

    if (isStrategyItemResponseDTO(benchmark.value)) return benchmark.value.code;

    return benchmark.value.portfolioId;
  });

  const activeReturnTrackColorMapId = computed((): string | null => {
    if (itemUnderAnalysisColorMapId.value === null || benchmarkColorMapId.value === null) return null;

    return `${itemUnderAnalysisColorMapId.value}-${benchmarkColorMapId.value}-active-return`;
  });

  return {
    itemUnderAnalysisColorMapId,
    benchmarkColorMapId,
    activeReturnTrackColorMapId,
  };
}
